// src/utils/api.ts
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { URLS } from "../constants";
import logger from "./logger";

/**
 * Extended Axios config that includes our custom retry flag
 */
interface RetryableConfig extends AxiosRequestConfig {
  __isRetry?: boolean;
}

/**
 * Helper function to read a cookie value by name
 * @param name - The name of the cookie to read
 * @returns The cookie value or null if not found
 */
export const getCookieValue = (name: string): string | null => {
  const cookies = document.cookie.split(";");
  const cookie = cookies.find((c) => c.trim().startsWith(`${name}=`));
  return cookie ? cookie.split("=")[1] : null;
};

/**
 * Helper function to sync fresh token from cookie to localStorage
 * @param token - The fresh token to store in localStorage
 */
export const syncTokenToLocalStorage = (token: string): void => {
  const authUser = localStorage.getItem("authUser");
  if (!authUser) {
    return;
  }

  try {
    const userData = JSON.parse(authUser);
    userData.token = token;
    localStorage.setItem("authUser", JSON.stringify(userData));
  } catch (e) {
    logger.error("Error updating authUser with fresh token:", e);
  }
};

/**
 * Helper function to clear all auth data and redirect to login
 */
export const clearAuthAndRedirect = (): void => {
  localStorage.removeItem("authUser");
  localStorage.removeItem("oauth_state");
  localStorage.removeItem("oauth_connector_id");

  document.cookie =
    "auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  document.cookie =
    "refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

  if (typeof window !== "undefined") {
    window.location.href = "/auth-login";
  }
};

/**
 * Central Axios instance for all API calls
 */
const api = axios.create({
  baseURL: `${URLS.CLIENT_URL}`,
  withCredentials: true,
});

let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
let lastRefreshTime = 0;
const REFRESH_COOLDOWN = 5000; // 5 seconds cooldown between refresh attempts

let isRefreshing = false;
type QueueCallback = (error: unknown | null) => void;
const failedQueue: QueueCallback[] = [];

/**
 * Request interceptor to add Authorization header
 */
api.interceptors.request.use(
  (config) => {
    // Check for local API key (for local development pointing to remote env)
    const localApiKey = process.env.REACT_APP_LOCAL_API_KEY;
    if (localApiKey) {
      config.headers.Authorization = `Bearer ${localApiKey}`;
      return config;
    }

    // Read token from HTTP-only cookie instead of localStorage
    // This ensures we use the latest token from the server (especially after refresh)
    const token = getCookieValue("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    }

    // Fallback to localStorage if no cookie found (for compatibility)
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        const userData = JSON.parse(authUser);
        if (userData?.token) {
          config.headers.Authorization = `Bearer ${userData.token}`;
        }
      } catch (e) {
        logger.error("Error parsing authUser data:", e);
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Process the queue of failed requests after token refresh completes
 */
const processQueue = (error: unknown | null) => {
  failedQueue.forEach((callback) => callback(error));
  failedQueue.length = 0; // Clear the queue
};

/**
 * Global response interceptor that implements the refresh flow
 */
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Reset refresh attempts on successful responses (except for refresh calls)
    if (
      response.config?.url &&
      !response.config.url.includes("/api/auth/refresh")
    ) {
      refreshAttempts = 0;
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as RetryableConfig;

    const is401 = error.response?.status === 401;
    const isRefreshCall = config?.url?.includes("/api/auth/refresh");
    const isAlreadyRetried = config?.__isRetry === true;

    // Prevent infinite loops: don't retry if already retried or if we've hit max attempts
    const now = Date.now();
    const isInCooldown = now - lastRefreshTime < REFRESH_COOLDOWN;

    if (
      is401 &&
      !isRefreshCall &&
      !isAlreadyRetried &&
      refreshAttempts < MAX_REFRESH_ATTEMPTS &&
      (!isInCooldown || isRefreshing) // Allow queueing even during cooldown if refresh is in progress
    ) {
      // CRITICAL FIX: Atomic check-and-set to prevent race conditions
      // Capture the current value and immediately set to true in one step
      const wasRefreshing = isRefreshing;
      isRefreshing = true;

      if (wasRefreshing) {
        // Another request is already refreshing, queue this one
        return new Promise((resolve, reject) => {
          failedQueue.push((error) => {
            if (error) {
              reject(error);
            } else {
              config.__isRetry = true;
              resolve(api(config));
            }
          });
        });
      }

      // This request is the first to arrive - it will do the refresh
      refreshAttempts++;
      lastRefreshTime = now;

      const refreshTokenBefore = getCookieValue("refresh_token");

      try {
        await api.post("/api/auth/refresh");

        // Sync fresh token from cookie to localStorage
        const freshToken = getCookieValue("access_token");
        if (freshToken) {
          syncTokenToLocalStorage(freshToken);
        }

        refreshAttempts = 0;
        processQueue(null); // Retry all queued requests with fresh token
        isRefreshing = false; // Reset AFTER processing queue
      } catch (refreshError) {
        const is401Refresh =
          (refreshError as { response?: { status?: number } })?.response
            ?.status === 401;
        const refreshTokenAfter = getCookieValue("refresh_token");
        const cookieWasUpdated = refreshTokenBefore !== refreshTokenAfter;

        if (is401Refresh && !cookieWasUpdated) {
          refreshAttempts = 0;
          clearAuthAndRedirect();
          processQueue(refreshError); // Reject all queued requests
          throw refreshError;
        }

        if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
          clearAuthAndRedirect();
        }

        processQueue(refreshError); // Reject all queued requests
        isRefreshing = false; // Reset AFTER processing queue
        throw refreshError;
      }

      // Retry the original request with the fresh token
      config.__isRetry = true;
      return api(config);
    }

    // For all other errors, or if we've already retried, propagate them
    return Promise.reject(error);
  },
);

export default api;
